import * as React from 'react';

import type { AuthenticatedPageProps } from '../api/serverSession/session.types';
import { QuickActionsGate } from '../components/quickActionsGate';
import { StaleBuildPromptGate } from '../components/staleBuildPromptGate';
import { ClarityProvider } from '../context/clarity.context';
import { FeatureFlagProvider } from '../context/featureFlag.context';
import { FirmProvider } from '../context/firm.context';
import { UserProvider } from '../context/user.context';
import { ViewedTransactionProvider } from '../context/viewedTransaction.context';
import { DOM_MUTATION_GUARD_FEATURE_FLAG, installDomMutationGuard } from '../utils/domMutationGuard';
import { STALE_BUILD_PROMPT_FEATURE_FLAG } from '../utils/staleBuildNavigation';

interface IProps {
  pageProps: AuthenticatedPageProps;
}

/** Providers that only make sense for a logged-in user. Must be rendered inside `AppProviders`. */
export const AuthenticatedProviders: React.FC<React.PropsWithChildren<IProps>> = ({
  pageProps: { session, firmSettings, canViewMigrations, clarityProjectId, enableQuickActions },
  children,
}) => {
  const domMutationGuardEnabled = !!session?.featureFlags?.includes(DOM_MUTATION_GUARD_FEATURE_FLAG);
  React.useEffect(() => {
    if (domMutationGuardEnabled) {
      installDomMutationGuard();
    }
  }, [domMutationGuardEnabled]);

  const staleBuildPromptEnabled = !!session?.featureFlags?.includes(STALE_BUILD_PROMPT_FEATURE_FLAG);

  return (
    <FeatureFlagProvider featureFlags={session.featureFlags}>
      <UserProvider scopes={session.scopes} authenticated canViewMigrations={canViewMigrations}>
        <FirmProvider firmSettings={firmSettings}>
          <ViewedTransactionProvider>
            <ClarityProvider userId={session.userId} clarityProjectId={clarityProjectId ?? undefined}>
              {children}
              <QuickActionsGate enabled={enableQuickActions} />
              <StaleBuildPromptGate enabled={staleBuildPromptEnabled} />
            </ClarityProvider>
          </ViewedTransactionProvider>
        </FirmProvider>
      </UserProvider>
    </FeatureFlagProvider>
  );
};
